'use client'

import cn from 'clsx'
import { useIntersectionObserver, useResizeObserver } from 'hamo'
import { useLenis } from 'lenis/react'
import {
  type HTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTempus } from 'tempus/react'
import { modulo } from '~/libs/maths'
import { mutate } from '~/libs/tempus-queue'
import s from './marquee.module.css'

export type MarqueeRef = {
  setSpeed: (speed: number) => void
}

interface MarqueeProps extends HTMLAttributes<HTMLDivElement> {
  repeat?: number
  speed?: number
  scrollVelocity?: boolean
  reversed?: boolean
  pauseOnHover?: boolean
  startFromZero?: boolean
  draggable?: boolean
}

export function Marquee({
  children,
  className,
  repeat = 2,
  speed = 1,
  scrollVelocity = true,
  reversed = false,
  pauseOnHover = false,
  startFromZero = false,
  draggable = false,
  onMouseEnter,
  onMouseLeave,
  ...props
}: MarqueeProps) {
  const [isDragging, setIsDragging] = useState(false)
  const refs = useRef({
    elements: [] as (HTMLDivElement | null)[],
    transform: startFromZero ? 0 : Math.random() * 1000,
    isHovered: false,
    dragStartX: 0,
    dragStartTransform: 0,
  }).current

  const [setRectRef, getEntry] = useResizeObserver({
    lazy: true,
  })

  const [setIntersectionRef, intersection] = useIntersectionObserver()
  const lenis = useLenis()

  const measureWidth = useCallback(() => {
    const entry = getEntry()
    return entry?.borderBoxSize[0]?.inlineSize ?? 0
  }, [getEntry])

  const updateTransforms = useCallback(() => {
    return mutate(() => {
      const transform = `translate3d(${-refs.transform}px,0,0)`
      for (const node of refs.elements) {
        if (node) node.style.transform = transform
      }
    })
  }, [refs])

  const handleDragStart = useCallback(
    (clientX: number) => {
      setIsDragging(true)
      refs.dragStartX = clientX
      refs.dragStartTransform = refs.transform
    },
    [refs]
  )

  const handleDragMove = useCallback(
    async (clientX: number) => {
      if (!isDragging) return

      refs.transform = refs.dragStartTransform + (refs.dragStartX - clientX)
      await updateTransforms()
    },
    [isDragging, refs, updateTransforms]
  )

  const handleDragEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  useTempus(async (_, deltaTime) => {
    if (
      !intersection?.isIntersecting ||
      (pauseOnHover && refs.isHovered) ||
      speed === 0 ||
      isDragging
    )
      return

    const width = await measureWidth()
    if (!width) return

    const velocity = scrollVelocity
      ? 1 + Math.abs((lenis?.velocity ?? 0) / 5)
      : 1
    const offset = deltaTime * (speed * 0.1 * velocity)

    refs.transform = modulo(
      refs.transform + (reversed ? -offset : offset),
      width
    )

    await updateTransforms()
  })

  useEffect(() => {
    return () => {
      refs.elements = []
    }
  }, [refs])

  return (
    <div
      ref={setIntersectionRef}
      className={cn(className, s.marquee, {
        [s.draggable]: draggable,
        [s.dragging]: isDragging,
      })}
      onMouseEnter={(e) => {
        refs.isHovered = true
        onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        refs.isHovered = false
        if (draggable) handleDragEnd()
        onMouseLeave?.(e)
      }}
      onMouseDown={draggable ? (e) => handleDragStart(e.clientX) : undefined}
      onMouseMove={draggable ? (e) => handleDragMove(e.clientX) : undefined}
      onMouseUp={draggable ? () => handleDragEnd() : undefined}
      onTouchStart={
        draggable ? (e) => handleDragStart(e.touches[0].clientX) : undefined
      }
      onTouchMove={
        draggable ? (e) => handleDragMove(e.touches[0].clientX) : undefined
      }
      onTouchEnd={draggable ? () => handleDragEnd() : undefined}
      style={
        draggable
          ? {
              userSelect: 'none',
            }
          : undefined
      }
      {...props}
    >
      {Array.from({ length: repeat }, (_, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: These elements are static repeats that never change order
          key={`marquee-${i}`}
          className={s.inner}
          aria-hidden={i !== 0}
          data-nosnippet={i !== 0 ? '' : undefined}
          ref={(node) => {
            if (!node) {
              refs.elements[i] = null
              return
            }
            refs.elements[i] = node
            if (i === 0) setRectRef(node)
          }}
        >
          {children}
        </div>
      ))}
    </div>
  )
}
